import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Shield, Eye, EyeOff, CheckCircle2, XCircle, Lock, AlertCircle } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Frontend-side mirror of backend `_validate_password` so the user gets
 *  immediate feedback. Backend re-validates on submit (defense-in-depth). */
function validatePassword(pwd) {
  return [
    { key: 'len',  ok: pwd.length >= 8,                                  label: 'En az 8 karakter / At least 8 characters' },
    { key: 'up',   ok: /[A-Z]/.test(pwd),                                label: '1 büyük harf / 1 uppercase' },
    { key: 'lo',   ok: /[a-z]/.test(pwd),                                label: '1 küçük harf / 1 lowercase' },
    { key: 'dig',  ok: /\d/.test(pwd),                                   label: '1 rakam / 1 digit' },
    { key: 'spec', ok: /[!@#$%^&*()_+\-=\[\]{}|;:,.<>?/~`'"\\]/.test(pwd), label: '1 özel karakter / 1 special char' },
  ];
}

export default function SuperAdminSetupPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') || '';

  // 'loading' | 'invalid' | 'ready' | 'submitting' | 'done'
  const [phase, setPhase] = useState('loading');
  const [info,  setInfo]  = useState(null);  // { email, full_name, expires_at }
  const [error, setError] = useState('');
  const [pwd, setPwd]     = useState('');
  const [pwd2, setPwd2]   = useState('');
  const [showPwd, setShowPwd] = useState(false);

  // Validate token on mount.
  useEffect(() => {
    if (!token) {
      setPhase('invalid');
      setError('Davet bağlantısı eksik. / Invitation link missing.');
      return;
    }
    axios.get(`${API}/auth/super-admin-setup/validate?token=${encodeURIComponent(token)}`)
      .then(r => {
        setInfo(r.data);
        setPhase('ready');
      })
      .catch(e => {
        setPhase('invalid');
        setError(e.response?.data?.detail || 'Davet geçersiz veya süresi dolmuş. / Invitation invalid or expired.');
      });
  }, [token]);

  const checks    = validatePassword(pwd);
  const allValid  = checks.every(c => c.ok);
  const matches   = pwd && pwd === pwd2;
  const canSubmit = allValid && matches && phase === 'ready';

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setPhase('submitting');
    setError('');
    try {
      await axios.post(`${API}/auth/super-admin-setup`, { token, password: pwd });
      setPhase('done');
      // Auto-redirect to login after a short pause so the user sees the success state.
      setTimeout(() => navigate('/giris?tab=ultra'), 2200);
    } catch (e) {
      setError(e.response?.data?.detail || 'Şifre belirlenemedi. / Could not set password.');
      setPhase('ready');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-violet-900 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md" data-testid="super-admin-setup-page">
        {/* Logo / heading */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-violet-500/20 border border-violet-400/30 rounded-3xl mb-5">
            <Shield className="w-10 h-10 text-violet-300" />
          </div>
          <h1 className="text-3xl font-bold text-white tracking-tight">Süper Admin · Setup</h1>
          <p className="text-violet-200/70 text-sm mt-2">COP 31 Antalya Platform</p>
        </div>

        {/* Body */}
        {phase === 'loading' && (
          <div className="bg-white/5 border border-white/10 backdrop-blur rounded-2xl p-8 text-center text-white/70">
            Davet doğrulanıyor… / Validating invitation…
          </div>
        )}

        {phase === 'invalid' && (
          <div className="bg-white/5 border border-red-400/30 backdrop-blur rounded-2xl p-8 text-center" data-testid="invalid-token-state">
            <XCircle className="w-14 h-14 text-red-400 mx-auto mb-4" />
            <h2 className="text-white text-lg font-bold mb-2">Bağlantı geçersiz</h2>
            <p className="text-red-200/80 text-sm mb-6">{error}</p>
            <button
              onClick={() => navigate('/')}
              className="bg-white/10 hover:bg-white/20 text-white text-sm rounded-xl px-5 py-2.5 transition-colors">
              Anasayfaya dön / Back to home
            </button>
          </div>
        )}

        {phase === 'done' && (
          <div className="bg-white/5 border border-emerald-400/30 backdrop-blur rounded-2xl p-8 text-center" data-testid="setup-done-state">
            <CheckCircle2 className="w-14 h-14 text-emerald-400 mx-auto mb-4" />
            <h2 className="text-white text-lg font-bold mb-2">Şifreniz oluşturuldu! / Password set!</h2>
            <p className="text-emerald-200/80 text-sm mb-3">
              Login sayfasına yönlendiriliyorsunuz… / Redirecting to login…
            </p>
            <p className="text-white/60 text-xs">
              Kullanıcı adı / Username:{' '}
              <span className="font-mono bg-white/10 px-2 py-0.5 rounded">{info?.email}</span>
            </p>
          </div>
        )}

        {(phase === 'ready' || phase === 'submitting') && (
          <form onSubmit={submit} className="bg-white/5 border border-white/10 backdrop-blur rounded-2xl p-6 space-y-4">
            {/* Welcome banner */}
            <div className="bg-violet-500/10 border border-violet-400/20 rounded-xl p-3 text-sm text-violet-100 flex gap-2">
              <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <div>
                Hoş geldin <strong>{info?.full_name || info?.email}</strong>.
                Şifren belirlendikten sonra
                <span className="font-mono bg-white/10 px-1.5 mx-1 rounded">{info?.email}</span>
                ile giriş yapabilirsin.
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="block text-xs text-white/70 mb-1.5">Yeni Şifre / New Password</label>
              <div className="relative">
                <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                <input
                  data-testid="new-password-input"
                  type={showPwd ? 'text' : 'password'}
                  value={pwd}
                  onChange={e => setPwd(e.target.value)}
                  className="w-full bg-white/10 border border-white/20 text-white placeholder-white/30 rounded-xl pl-9 pr-10 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-400 focus:border-violet-400"
                  placeholder="••••••••"
                  required />
                <button
                  type="button"
                  onClick={() => setShowPwd(s => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white">
                  {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Confirm */}
            <div>
              <label className="block text-xs text-white/70 mb-1.5">Şifre Tekrarı / Confirm</label>
              <div className="relative">
                <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                <input
                  data-testid="confirm-password-input"
                  type={showPwd ? 'text' : 'password'}
                  value={pwd2}
                  onChange={e => setPwd2(e.target.value)}
                  className="w-full bg-white/10 border border-white/20 text-white placeholder-white/30 rounded-xl pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-400 focus:border-violet-400"
                  placeholder="••••••••"
                  required />
              </div>
              {pwd2 && !matches && (
                <p className="text-rose-300 text-[11px] mt-1">Şifreler eşleşmiyor / Passwords do not match</p>
              )}
            </div>

            {/* Validation checklist */}
            <div className="bg-white/5 rounded-xl p-3 space-y-1.5">
              {checks.map(c => (
                <div key={c.key} className="flex items-center gap-2 text-xs">
                  {c.ok
                    ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400 flex-shrink-0" />
                    : <div className="h-3.5 w-3.5 rounded-full border border-white/20 flex-shrink-0" />}
                  <span className={c.ok ? 'text-emerald-200' : 'text-white/50'}>{c.label}</span>
                </div>
              ))}
            </div>

            {error && (
              <div className="bg-red-500/10 border border-red-400/30 text-red-200 text-sm px-3 py-2.5 rounded-xl">
                {error}
              </div>
            )}

            <button
              type="submit"
              data-testid="set-password-submit"
              disabled={!canSubmit || phase === 'submitting'}
              className="w-full py-3 rounded-xl bg-violet-600 hover:bg-violet-700 text-white text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
              {phase === 'submitting' ? 'Kaydediliyor… / Saving…' : 'Şifreyi Belirle / Set Password'}
            </button>
          </form>
        )}

        <p className="text-white/40 text-[11px] text-center mt-6">
          Bu davet bağlantısı 24 saat geçerlidir. / This invitation is valid for 24 hours.
        </p>
      </div>
    </div>
  );
}
